import { inject, Injectable } from '@angular/core';
import { FUSE_CONFIG } from '@fuse/services/config/config.constants';
import { merge } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FuseConfigService {
    private readonly _storageKeys = {
        scheme: 'scheme',
        theme: 'theme',
    } as const;

    private readonly _defaultConfig = inject(FUSE_CONFIG);

    private _config = new BehaviorSubject(
        merge({}, this._defaultConfig, this._readStoredConfig())
    );

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for config
     */
    set config(value: any) {
        // Merge the new config over to the current config
        const config = merge({}, this._config.getValue(), value);

        // Persist user selections (desktop/web) so they survive reloads
        this._persistConfig(config);

        // Execute the observable
        this._config.next(config);
    }

    get config(): any {
        return this._config.getValue();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get config$(): Observable<any> {
        return this._config.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Resets the config to the default
     */
    reset(): void {
        // Reset back to defaults and clear persisted overrides
        this._clearPersistedConfig();
        this._config.next(merge({}, this._defaultConfig));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _readStoredConfig(): any {
        // Guard for environments where storage isn't available
        if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
            return {};
        }

        try {
            const schemeRaw = globalThis.localStorage.getItem(
                this._storageKeys.scheme
            );
            const themeRaw = globalThis.localStorage.getItem(
                this._storageKeys.theme
            );

            const stored: any = {};

            if (schemeRaw === 'auto' || schemeRaw === 'dark' || schemeRaw === 'light') {
                stored.scheme = schemeRaw;
            }

            if (typeof themeRaw === 'string' && themeRaw.length > 0) {
                stored.theme = themeRaw;
            }

            return stored;
        } catch {
            return {};
        }
    }

    private _persistConfig(config: any): void {
        if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
            return;
        }

        try {
            if (config?.scheme) {
                globalThis.localStorage.setItem(
                    this._storageKeys.scheme,
                    String(config.scheme)
                );
            }

            if (config?.theme) {
                globalThis.localStorage.setItem(
                    this._storageKeys.theme,
                    String(config.theme)
                );
            }
        } catch {
            // Ignore storage write errors (e.g., storage disabled)
        }
    }

    private _clearPersistedConfig(): void {
        if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
            return;
        }

        try {
            globalThis.localStorage.removeItem(this._storageKeys.scheme);
            globalThis.localStorage.removeItem(this._storageKeys.theme);
        } catch {
            // Ignore storage errors
        }
    }
}
